/**
 * 获取实际的滚动容器
 * 全屏模式下，滚动发生在 container (.doc-container) 上
 * 非全屏单页模式下，滚动发生在 .pdf-list-horizontal 上（单页内容与缩放滚动均在此）
 * 非全屏若无 horizontal 则回退到 .pdf-content-wrap
 */
export function getScrollContainer(container: HTMLElement | null, screenState: string): HTMLElement | null {
    if (!container) return null;
    if (screenState === 'full') return container;
    const horizontalList = container.querySelector('.pdf-list-horizontal') as HTMLElement | null;
    const contentWrap = container.querySelector('.pdf-content-wrap') as HTMLElement | null;
    const horizontalHasOverflow =
        horizontalList &&
        (horizontalList.scrollHeight > horizontalList.clientHeight ||
            horizontalList.scrollWidth > horizontalList.clientWidth);
    if (horizontalHasOverflow) return horizontalList;
    if (contentWrap) return contentWrap;
    return container;
}

/** scroll-idle 判定阈值（ms）：超过此时长无新 scroll 事件则认为滚动已停止 */
const SCROLL_IDLE_MS = 80;

/**
 * 等待容器滚动停止。
 *
 * 策略：
 * 1. 优先使用 scrollend 原生事件（Chrome 109+ / Safari 17.4+）
 * 2. 降级：监听 scroll 事件 + idle 检测（超过 SCROLL_IDLE_MS 无新事件则 resolve）
 *
 * 支持通过 AbortSignal 取消，组件卸载或新跳转触发时应调用 AbortController.abort()。
 */
export function waitForScrollEnd(container: HTMLElement, signal?: AbortSignal): Promise<void> {
    return new Promise((resolve) => {
        if (signal?.aborted) {
            resolve();
            return;
        }

        let onScrollEnd: (() => void) | null = null;
        let onScroll: (() => void) | null = null;

        const cleanup = () => {
            if (onScrollEnd) container.removeEventListener('scrollend', onScrollEnd);
            if (onScroll) container.removeEventListener('scroll', onScroll);
            signal?.removeEventListener('abort', onAbort);
        };
        const done = () => {
            cleanup();
            resolve();
        };
        const onAbort = () => done();

        // 策略 1：scrollend 原生事件（现代浏览器）
        if ('onscrollend' in window) {
            onScrollEnd = () => done();
            container.addEventListener('scrollend', onScrollEnd, { once: true });
            signal?.addEventListener('abort', onAbort, { once: true });
            return;
        }

        // 策略 2：scroll idle 检测降级
        let idleTimer: ReturnType<typeof setTimeout> | null = null;
        onScroll = () => {
            if (idleTimer !== null) clearTimeout(idleTimer);
            idleTimer = setTimeout(done, SCROLL_IDLE_MS);
        };
        container.addEventListener('scroll', onScroll, { passive: true });
        signal?.addEventListener('abort', onAbort, { once: true });
        // 若容器当前静止（首次即无滚动），触发一次 idle 计时兜底
        onScroll();
    });
}

/**
 * 等待两帧渲染完成（layout + paint），比 setTimeout(50) 更精确。
 * 用于需要等待 DOM 布局稳定后再操作的场景（如 compensate 解锁）。
 */
export function waitTwoFrames(): Promise<void> {
    return new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
}

/**
 * 重置所有相关容器的滚动位置
 */
export function resetAllScrollPositions(container: HTMLElement | null): void {
    if (!container) {
        return;
    }

    // 重置主容器的滚动位置
    container.scrollTop = 0;
    container.scrollLeft = 0;

    // 重置 .pdf-content-wrap 的滚动位置
    const contentWrap = container.querySelector('.pdf-content-wrap') as HTMLElement;
    if (contentWrap) {
        contentWrap.scrollTop = 0;
        contentWrap.scrollLeft = 0;
    }

    // 重置 .pdf-list-horizontal 的滚动位置
    const horizontalList = container.querySelector('.pdf-list-horizontal') as HTMLElement;
    if (horizontalList) {
        horizontalList.scrollTop = 0;
        horizontalList.scrollLeft = 0;
    }
}
